import snakeCase from 'lodash/snakeCase';
import isPlainObject from 'lodash/isPlainObject';
import transform from 'lodash/transform';
import camelCase from 'lodash/camelCase';
import isObject from 'lodash/isObject';
import { getOcbDomain } from 'utils/getDomains';

const MAX_SUBDOMAIN_LENGTH = 30;
const SPLASH_DOMAIN_NAME = `.${getOcbDomain()}`;

/**
 * Format a URL. Removes accented characters, lower cases, and trims.
 * @param {String} urlString
 * @param {number} [maxLength]
 * @return {string}
 */
export function formatUrl(urlString = '', maxLength = false) {
    let formattedString = urlString
        .trim()
        .replace(/[ñń]/gi, 'n')
        .replace(/[üûùúū]/gi, 'u')
        .replace(/[öõôòóœøōõ]/gi, 'o')
        .replace(/[ïîíīįì]/gi, 'i')
        .replace(/[ëêèéēėę]/gi, 'e')
        .replace(/[ÿ]/gi, 'y')
        .replace(/[ãäâàáæå]/gi, 'a')
        .replace(/[^a-z0-9-.]/gi, '');

    formattedString = formattedString.toLowerCase();

    if (maxLength && typeof maxLength === 'number') {
        formattedString = formattedString.substring(0, maxLength);
    }

    return formattedString;
}

/**
 * Checks if string is a valid email address
 * @param  {String}  val
 * @return {bool}
 */
export function isValidEmail(val = '') {
    const reg = /^[^@\s]+@[^.\s]+\.[^\s@,]+$/;

    if (!val || typeof val !== 'string') {
        return false;
    }

    return reg.test(val.trim());
}

/**
 * Formats a possible Splash subdomain. Follows same rules as
 * formatting URLs, but with extra formatting.
 * @param  {string} urlString
 * @return {string}
 */
export function formatSplashSubdomain(subdomain = '') {
    let formattedString = formatUrl(subdomain, MAX_SUBDOMAIN_LENGTH);
    formattedString = formattedString.replace(/\./gi, '');
    return formattedString;
}

export function getSplashSubdomainFromUrl(url, domainName = SPLASH_DOMAIN_NAME) {
    return url.replace(domainName, '');
}

export function snakeCaseToCamelCase(string = '') {
    return string.replace(/(_\w)/g, (m) => m[1].toUpperCase());
}

export function camelCaseToSnakeCase(string = '') {
    return snakeCase(string);
}

export function parseKeysSnakeCaseToCamelCase(obj, reverse, valueParser = (value) => value) {
    return Object.keys(obj || {}).reduce((acc, key) => {
        if (!{}.hasOwnProperty.call(obj, key)) return acc;

        const newKey = !reverse ? snakeCaseToCamelCase(key) : camelCaseToSnakeCase(key);
        return {
            ...acc,
            [newKey]: valueParser(obj[key]),
        };
    }, {});
}

export function parseObjectSnakeCaseToCamelCaseRecursive(obj, reverse, valueParser = (value) => value) {
    const innerConvert = Object.keys(obj || {}).reduce(
        (acc, key) =>
            isPlainObject(obj[key])
                ? {
                      ...acc,
                      [key]: parseObjectSnakeCaseToCamelCaseRecursive(obj[key], reverse, valueParser),
                  }
                : {
                      ...acc,
                      [key]: obj[key],
                  },
        {},
    );

    return parseKeysSnakeCaseToCamelCase(innerConvert, reverse, valueParser);
}

export const parseKeysSnakeCaseToCamelCaseRecursive = (obj) => parseObjectSnakeCaseToCamelCaseRecursive(obj);

const parseArrayValues = (value) => (Array.isArray(value) ? value.map((item) => snakeCaseToCamelCase(item)) : value);

export const parseObjectWithArraySnakeCaseToCamelCaseRecursive = (obj) =>
    parseObjectSnakeCaseToCamelCaseRecursive(obj, undefined, parseArrayValues);

export function parseKeysCamelCaseToSnakeCase(obj) {
    return parseKeysSnakeCaseToCamelCase(obj, true);
}

export function parseDbString(string) {
    try {
        return decodeURIComponent(escape(string));
    } catch (e) {
        return string;
    }
}

// TODO: We can probably replace some of the above functions with this one
export const camelize = (obj) =>
    transform(obj, (acc, value, key, target) => {
        const camelKey = Array.isArray(target) ? key : camelCase(key);
        acc[camelKey] = isObject(value) ? camelize(value) : value;
    });
